<style>
    @keyframes highlight {
        0% {
            background-color: white;
        }
        25% {
            background-color: #f7f7f7;
        }
        50% {
            background-color: #f7f7f7;
        }
        75% {
            background-color: #f7f7f7;
        }
        100% {
            background-color: white;
        }
    }

    @keyframes shakeup {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(-2px);
        }
        50% {
            transform: translateY(-4px);
        }
        75% {
            transform: translateY(-6px);
        }
        100% {
            transform: translateY(-8px);
        }
    }
    .shake-up {
        animation: shakeup 0.2s ease-in-out forwards;
    }

    @keyframes shakedown {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(2px);
        }
        50% {
            transform: translateY(4px);
        }
        75% {
            transform: translateY(6px);
        }
        100% {
            transform: translateY(8px);
        }
    }
    .shake-down {
        animation: shakedown 0.2s ease-in-out forwards;
    }
    .mtapp-mfp button.mtapp-disabled-button {
        cursor: initial !important;
        opacity: 0.6 !important;
        border-color: #a5a5a5 !important;
        background-color: #ffffff !important;
    }
    .mtapp-mfp button[disabled] {
        cursor: initial !important;
        opacity: 0.6 !important;
    }
</style>

<script>
    import MultiFieldAsset from './MultiFieldAsset.svelte';
    import MultiFieldContentData from './MultiFieldContentData.svelte';
    import MultiFieldInput from './MultiFieldInput.svelte';
    import MultiFieldTable from './MultiFieldTable.svelte';
    import MultiFieldTableVertical from './MultiFieldTableVertical.svelte';
    import { toJSONString } from '../../../toJSONString.js';
    import { temporaryId } from '../../../temporaryId.js';
    import { modalConfirm } from '../../../modalConfirm.js';
    import { modalMsg } from '../../../modalMsg.js';
    import { onMount, untrack } from 'svelte';

    // ----------------------------------------
    // props で受け取る変数
    // ----------------------------------------

    /**
     * @typedef {Object} Props
     * @property {any} mtappVars
     * @property {any} data
     * @property {any} isRequired
     * @property {any} objectType
     * @property {any} options
     */

    /** @type {Props} */
    let { mtappVars: mtappVarsProp, data = $bindable(), isRequired, objectType, options = $bindable() } = $props();
    // mtappVars はコンポーネント生存中は不変。untrack で初期値に固定する。
    const mtappVars = untrack(() => mtappVarsProp);

    // ----------------------------------------
    // ローカル変数
    // ----------------------------------------
    let staticPath = mtappVars.static_path;
    let isDebug = $state(options.debug);
    let insertIndex = $state(-1);
    let insertDirection = $state('before');
    let hasMultiFieldComponentsV1 = typeof window?.customElements?.get('multifield-components-v1') !== 'undefined';
    // 各ウェブコンポーネントへの参照を保持する配列
    let webComponents = $state([]);
    let itemsLength = data.items.length;

    // ----------------------------------------
    // リアクティブな変数
    // ----------------------------------------
    let jsonData = $state('');
    let disabledAdd = $derived(typeof options.maxBlocks === 'number' && options.maxBlocks > 0 && options.maxBlocks <= data.items.length);
    let disabledRemove = $derived(typeof options.minBlocks === 'number' && options.minBlocks > 0 && options.minBlocks >= data.items.length);
    let showAddContainer = $state(options.showAddContainer);

    $effect(() => {
        jsonData = JSON.stringify(data);
    });

    // ----------------------------------------
    // メソッド
    // ----------------------------------------
    /**
     * ブロックを上に移動する。
     * @param {number} i
     */
    function blockUp(i) {
        data.items[i - 1].shakeDown = true;
        data.items[i].shakeUp = true;
        data = { ...data };
        setTimeout(() => {
            data.items.splice(i - 1, 2, data.items[i], data.items[i - 1]);
            delete data.items[i - 1].shakeUp;
            delete data.items[i].shakeDown;
            data = { ...data };
        }, 200);
    }

    /**
     * ブロックを下に移動する。
     * @param {number} i
     */
    function blockDown(i) {
        data.items[i].shakeDown = true;
        data.items[i + 1].shakeUp = true;
        data = { ...data };
        setTimeout(() => {
            data.items.splice(i, 2, data.items[i + 1], data.items[i]);
            delete data.items[i].shakeUp;
            delete data.items[i + 1].shakeDown;
            data = { ...data };
        }, 200);
    }

    /**
     * table 内の各フィールド（セル）に ID を付与する
     * @param {array} data
     * @returns {*}
     */
    function setIdToTableField(data) {
        for (let i = 0, l = data.length; i < l; i++) {
            for (let x = 0, y = data[i].length; x < y; x++) {
                data[i][x].id = temporaryId();
            }
        }
        return data;
    }

    /**
     * ブロックを挿入する。
     * @param {Object} field
     */
    function blockAdd(field, index = -1, direction = 'after') {
        if (options.maxBlocks && options.maxBlocks <= data.items.length) {
            modalMsg({
                type: options.maxBlocksMessage.type,
                title: options.maxBlocksMessage.title,
                content: options.maxBlocksMessage.content.replace(/%maxblocks/gi, options.maxBlocks)
            });
            return;
        }
        let newItem = JSON.parse(toJSONString(field));
        newItem.id = temporaryId();
        if (typeof newItem.data === 'undefined') {
            newItem.data = '';
        }
        let childData;
        if (field.type === 'table') {
            newItem.data = JSON.parse('[' + toJSONString(field.options) + ']');
            newItem.data = setIdToTableField(newItem.data);
        } else if (field.type.startsWith('table-vertical')) {
            newItem.data = JSON.parse(toJSONString(field.options));
            for (let i = 0, l = newItem.data.length; i < l; i++) {
                newItem.data[i].id = temporaryId();
            }
        } else if (field.type === 'multi-column') {
            newItem.data = JSON.parse(toJSONString(field.options));
            for (let i = 0, l = newItem.data.length; i < l; i++) {
                if (newItem.data[i].type === 'table') {
                    childData = JSON.parse('[' + toJSONString(newItem.data[i].options) + ']');
                    newItem.data[i].data = setIdToTableField(childData);
                }
                newItem.data[i].id = temporaryId();
            }
        } else if (field.type === 'multi-column-content') {
            newItem.data = JSON.parse(toJSONString(field.options));
            for (let i = 0; i < newItem.data.length; i++) {
                for (let j = 0; j < newItem.data[i].length; j++) {
                    if (newItem.data[i][j].type === 'table') {
                        childData = JSON.parse('[' + toJSONString(newItem.data[i][j].options) + ']');
                        newItem.data[i][j].data = setIdToTableField(childData);
                    }
                    newItem.data[i][j].id = temporaryId();
                }
            }
        } else if (field.type === 'contentData') {
            newItem.data = [];
        } else if (field.type === 'listing') {
            newItem.data = [];
        }

        if (index === -1) {
            data.items.push(newItem);
        } else {
            let pointerIndex = index + (direction === 'after' ? 1 : 0);
            data.items.splice(pointerIndex, 0, newItem);
        }
        data = data;
    }

    /**
     * ブロックを削除する。
     * @param {number} i
     */
    function blockRemove(i) {
        modalConfirm({
            type: 'warning',
            title: '警告',
            content: '削除しますか？',
            okButtonLabel: 'OK',
            cancelButtonLabel: 'キャンセル',
            callback: function () {
                data.items.splice(i, 1);
                data = { ...data };
            }
        });
    }

    /**
     * フィールドのラベルを取得する。
     * @param {Object} field
     */
    function addButtonLabel(field) {
        if (typeof field !== 'object') {
            throw new Error('Field is not found.', field);
        }
        return field.addButtonLabel || field.label;
    }

    /**
     * マルチカラムのカラムのクラスを設定する。
     * @param {object} column
     */
    function setColumnClass(column) {
        const classes = ['mb-5', 'col'];
        if (column.col) {
            classes.push('col-' + column.col);
        }
        if (column.class) {
            classes.push(column.class);
        }
        return classes.join(' ');
    }

    /**
     * フィールドを追加するモーダルを表示する。
     */
    function showAddFieldModal(index) {
        insertIndex = index;
        const modalElement = document.getElementById('mtapp-mf-modal-' + options.id);
        // 毎回 new すると、開くアニメーション中の Enter キーで背景レイヤーが二重になり閉じても残るため、同じインスタンスを使う
        const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
        modal.show();
    }

    /**
     * ウェブコンポーネントにデータを渡す。
     */
    function setDataToWebComponents() {
        for (const [id, component] of Object.entries(webComponents)) {
            if (!component) {
                delete webComponents[id];
                continue;
            }
            const item = data.items.find((item) => item.id === id);
            component.mtappVars = mtappVars;
            component.item = item;
            component.id = item.id;
        }
    }

    onMount(() => {
        itemsLength = data.items.length;
        setDataToWebComponents();
    });

    $effect(() => {
        if (data.items.length > itemsLength) {
            setDataToWebComponents();
        }
        itemsLength = data.items.length;
    });
</script>

<div class="card mtapp-mf-container mb-5 mtapp-mfp">
    <!-- Card Header -->
    <div class="card-header d-flex justify-content-between align-items-center">
        <div>
            <span class="fw-bold">{options.label}</span>
            {#if isRequired}
                <span class="badge badge-danger">{mtappVars.translate['Required']}</span>
            {/if}
            {#if isDebug}
                <span class="badge badge-warning">{mtappVars.translate['Debug Mode']}</span>
                <span class="badge badge-warning">{`v${options.version}`}</span>
            {/if}
        </div>
    </div>
    <!-- END Card Header -->

    <!-- Card Body -->
    <div class="card-body">
        <textarea
            rows="10"
            class="form-control mb-5 font-monospace"
            name={objectType === 'content_data' ? 'content-field-' + options.id : options.id}
            class:d-none={!isDebug}
            bind:value={jsonData}></textarea>

        <div class="row">
            {#each data.items as item, itemIdx (item.id)}
                <div class="col-12 mtapp-mf-block">
                    <div
                        id={item.id + '-container'}
                        class="form-group"
                        class:shake-up={typeof item.shakeUp !== 'undefined' && item.shakeUp}
                        class:shake-down={typeof item.shakeDown !== 'undefined' && item.shakeDown}>
                        <div class="row justify-content-between">
                            <div class="col-sm-12 col-md-8">
                                <span class="d-inline-block mb-3 fw-bold">{item.label}</span>
                                {#if item.description}
                                    <p class="mtapp-mf-description mb-3">{item.description}</p>
                                {/if}
                            </div>
                            <div class="col-sm-12 col-md-4 mb-2 text-right">
                                <div role="group" class="btn-group btn-group-sm">
                                    {#if options.showSortButton}
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            disabled={itemIdx === 0}
                                            class="btn btn-default"
                                            onclick={() => {
                                                blockUp(itemIdx);
                                            }}
                                            aria-label="Move Up">
                                            <svg role="img" class="mt-icon mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-up'}></use>
                                            </svg>
                                        </button>
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            disabled={itemIdx === data.items.length - 1}
                                            class="btn btn-default"
                                            onclick={() => {
                                                blockDown(itemIdx);
                                            }}
                                            aria-label="Move Down">
                                            <svg role="img" class="mt-icon mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_tri-down'}></use>
                                            </svg>
                                        </button>
                                    {/if}
                                    {#if options.showInsertButton !== false}
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            class="btn btn-default"
                                            disabled={disabledAdd}
                                            onclick={() => {
                                                showAddFieldModal(itemIdx);
                                            }}
                                            aria-label="Add">
                                            <svg role="img" class="mt-icon mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_add'}></use>
                                            </svg>
                                        </button>
                                    {/if}
                                    {#if options.showRemoveButton !== false}
                                        <button
                                            tabindex="-1"
                                            type="button"
                                            class="btn btn-default"
                                            disabled={disabledRemove}
                                            onclick={() => {
                                                blockRemove(itemIdx);
                                            }}
                                            aria-label="Remove">
                                            <svg role="img" class="mt-icon mr-0">
                                                <use xlink:href={staticPath + '/images/sprite.svg#ic_trash'}></use>
                                            </svg>
                                        </button>
                                    {/if}
                                </div>
                            </div>
                        </div>
                        <div id={item.id + '-content'}>
                            {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(item.type)}
                                <MultiFieldInput mtappVars={mtappVars} item={item} on:inputchange={() => (data = { ...data })} />
                            {:else if ['asset', 'image'].includes(item.type)}
                                <MultiFieldAsset
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    item={item}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if item.type === 'contentData'}
                                <MultiFieldContentData
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    item={item}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if item.type === 'table'}
                                <MultiFieldTable
                                    mtappVars={mtappVars}
                                    id={item.id}
                                    item={item}
                                    on:inputchange={() => (data = { ...data })} />
                            {:else if ['table-vertical', 'table-vertical-fixed'].includes(item.type)}
                                <MultiFieldTableVertical mtappVars={mtappVars} item={item} on:inputchange={() => (data = { ...data })} />
                            {:else if item.type === 'multi-column'}
                                <div class="row" id={item.id}>
                                    {#each item.data as column, columnIdx (`${item.id}-${columnIdx}`)}
                                        <div class={setColumnClass(column)} data-column={columnIdx}>
                                            {#if column.label}
                                                <div class="mtapp-mf-column-label mb-3 mb-0 fw-bold">{@html column.label}</div>
                                            {/if}
                                            {#if column.description}
                                                <div class="mtapp-mf-column-description mb-3 mb-0">{@html column.description}</div>
                                            {/if}
                                            <div class="my-3">
                                                {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(column.type)}
                                                    <MultiFieldInput
                                                        mtappVars={mtappVars}
                                                        item={column}
                                                        on:inputchange={() => (data = { ...data })} />
                                                {:else if ['asset', 'image'].includes(column.type)}
                                                    <MultiFieldAsset
                                                        mtappVars={mtappVars}
                                                        id={column.id}
                                                        item={column}
                                                        on:inputchange={() => (data = { ...data })} />
                                                {/if}
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                            {:else if item.type === 'multi-column-content'}
                                <div class="row" id={item.id}>
                                    {#each item.data as columnGroup, columnGroupIdx (`${item.id}-${columnGroupIdx}`)}
                                        <div class="mb-5 col">
                                            {#each columnGroup as column, columnIdx (`${item.id}-${columnIdx}`)}
                                                <div class={setColumnClass(column)} data-column={columnIdx}>
                                                    {#if column.label}
                                                        <div class="mtapp-mf-column-label mb-3 mb-0 fw-bold">{@html column.label}</div>
                                                    {/if}
                                                    {#if column.description}
                                                        <div class="mtapp-mf-column-description mb-3 mb-0">{@html column.description}</div>
                                                    {/if}
                                                    <div class="my-3">
                                                        {#if ['text', 'email', 'url', 'tel', 'password', 'number', 'datepicker', 'date', 'time', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'textarea', 'code', 'tinymce', 'select', 'select-multiple', 'checkbox', 'radio'].includes(column.type)}
                                                            <MultiFieldInput
                                                                mtappVars={mtappVars}
                                                                item={column}
                                                                on:inputchange={() => (data = { ...data })} />
                                                        {:else if ['asset', 'image'].includes(column.type)}
                                                            <MultiFieldAsset
                                                                mtappVars={mtappVars}
                                                                id={column.id}
                                                                item={column}
                                                                on:inputchange={() => (data = { ...data })} />
                                                        {/if}
                                                    </div>
                                                </div>
                                            {/each}
                                        </div>
                                    {/each}
                                </div>
                            {:else if hasMultiFieldComponentsV1}
                                <multifield-components-v1 bind:this={webComponents[item.id]} oninputchange={() => (data = { ...data })}
                                ></multifield-components-v1>
                            {/if}
                        </div>
                        {#if item.help}
                            <div class="mt-3 text-muted">{item.help}</div>
                        {/if}
                    </div>
                </div>
            {/each}
        </div>
    </div>
    <!-- END Card Body -->

    <!-- Card Footer -->
    {#if (!disabledAdd || options.maxBlocksType !== 'hidden') && (options.showAddFieldButton || options.showViewRawDataButton || options.showAddContainer)}
        <div class="card-footer">
            {#if options.showAddFieldButton || options.showViewRawDataButton}
                <div class="btn-group" role="group">
                    {#if options.showAddFieldButton}
                        <button
                            type="button"
                            class="btn btn-default d-flex align-items-center"
                            onclick={() => (showAddContainer = !showAddContainer)}>
                            <svg role="img" class="mt-icon">
                                <use xlink:href={staticPath + '/images/sprite.svg#ic_add'}></use>
                            </svg>
                            {options.addFieldButtonLabel || mtappVars.translate['Add Field']}
                        </button>
                    {/if}
                    {#if options.showViewRawDataButton}
                        <button
                            type="button"
                            class="btn d-flex align-items-center"
                            class:btn-primary={isDebug}
                            class:btn-default={!isDebug}
                            onclick={() => (isDebug = !isDebug)}>
                            <svg role="img" class="mt-icon">
                                <use xlink:href={staticPath + '/images/sprite.svg#ic_preview'}></use>
                            </svg>
                            {mtappVars.translate['View Raw Data']}
                        </button>
                    {/if}
                </div>
            {/if}
            {#if showAddContainer}
                {#each options.fieldGroups as fieldGroup, fieldGroupIdx (fieldGroupIdx)}
                    <div class="btn-group d-block my-3" role="group">
                        {#each fieldGroup as field, fieldIdx (`${fieldGroupIdx}-${fieldIdx}`)}
                            <button
                                type="button"
                                class="btn btn-default"
                                class:mtapp-disabled-button={disabledAdd}
                                onclick={() => {
                                    blockAdd(field);
                                }}>
                                {addButtonLabel(field)}
                            </button>
                        {/each}
                    </div>
                {/each}
            {/if}
        </div>
    {/if}
    <!-- END Card Footer -->
</div>

<!-- モーダル -->
<div class="modal fade" role="dialog" id={'mtapp-mf-modal-' + options.id} aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h5 class="modal-title">{options.addFieldButtonLabel || mtappVars.translate['Add Field']}</h5>
            </div>
            <div class="modal-body">
                <div class="form-check form-check-inline">
                    <input
                        id={'mtapp-mf-modal-ins-above-' + options.id}
                        class="form-check-input"
                        type="radio"
                        bind:group={insertDirection}
                        value="before"
                        checked />
                    <label class="form-check-label" for={'mtapp-mf-modal-ins-above-' + options.id}>
                        {mtappVars.translate['Insert above']}
                    </label>
                </div>
                <div class="form-check form-check-inline">
                    <input
                        id={'mtapp-mf-modal-ins-below-' + options.id}
                        class="form-check-input"
                        type="radio"
                        bind:group={insertDirection}
                        value="after" />
                    <label class="form-check-label" for={'mtapp-mf-modal-ins-below-' + options.id}>
                        {mtappVars.translate['Insert below']}
                    </label>
                </div>
                <hr class="me-2" />
                {#each options.fieldGroups as fieldGroup, fieldGroupIdx (fieldGroupIdx)}
                    <div class="btn-group d-block my-3" role="group">
                        {#each fieldGroup as field, fieldIdx (`${fieldGroupIdx}-${fieldIdx}`)}
                            <button
                                type="button"
                                class="btn btn-default"
                                data-bs-dismiss="modal"
                                class:mtapp-disabled-button={disabledAdd}
                                onclick={() => {
                                    blockAdd(field, insertIndex, insertDirection);
                                }}>
                                {addButtonLabel(field)}
                            </button>
                        {/each}
                    </div>
                {/each}
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                    {mtappVars.translate['Cancel']}
                </button>
            </div>
        </div>
    </div>
</div>
<!-- END モーダル -->
